import { formatPhoneForDisplay, parseThreadId, REGISTRANT_TYPE_LABELS, threadIdFor } from '@crew/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import { ChatView, type ChatLine } from '@/components/chat/ChatView';
import { BackIcon, ChatIcon, PlusIcon, SearchIcon } from '@/components/icons';
import { TypeChip } from '@/components/jobs/RegistrationsPanel';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Notice } from '@/components/ui/Notice';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { useJob, useJobRegistrations } from '@/hooks/useActiveJobs';
import {
  isThreadUnread,
  useActiveRegistrants,
  useTeamChat,
  useTeamReadAt,
  useThread,
  useThreadMessages,
  useThreads,
  useUnreadMessages,
  type ThreadView,
} from '@/hooks/useMessaging';
import { formatWhen } from '@/lib/activity';
import { formatJobDate } from '@/lib/format';
import { tsToDate } from '@/lib/ts';
import { adminApi, readerUid } from '@/services/adminApi';
import { errorMessage } from '@/services/apiError';

/**
 * הודעות: שתי לשוניות —
 * "נרשמים": שיחה פרטית עם כל נרשם בכל עבודה (כל המנהלים רואים ועונים),
 * "צוות המנהלים": צ'אט פנימי, הנרשמים לא רואים אותו.
 */
export function MessagesPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'team' ? 'team' : 'threads';
  const unread = useUnreadMessages();
  const [picking, setPicking] = useState(false);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="הודעות"
        actions={
          <Button className="min-h-10 px-3 text-[14px]" icon={<PlusIcon size={17} />} onClick={() => setPicking(true)}>
            שיחה חדשה
          </Button>
        }
      />
      <NewChatDialog open={picking} onClose={() => setPicking(false)} />
      <div role="tablist" className="mb-4 grid grid-cols-2 gap-1 rounded-control bg-sunken p-1">
        <TabButton active={tab === 'threads'} count={unread.threads} onClick={() => setParams({}, { replace: true })}>
          נרשמים
        </TabButton>
        <TabButton active={tab === 'team'} count={unread.team} onClick={() => setParams({ tab: 'team' }, { replace: true })}>
          צוות המנהלים
        </TabButton>
      </div>
      {tab === 'threads' ? <ThreadList /> : <TeamChat />}
    </div>
  );
}

function TabButton({ active, count, onClick, children }: { active: boolean; count: number; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`flex min-h-11 items-center justify-center gap-2 rounded-[8px] text-[15px] font-semibold ${active ? 'bg-surface text-ink shadow-sm' : 'text-ink-2'}`}
    >
      {children}
      {count > 0 && <span className="tabular min-w-5 rounded-full bg-accent px-1.5 text-[12px] leading-5 text-white">{count}</span>}
    </button>
  );
}

function ThreadList() {
  const { user } = useAdmin();
  const uid = readerUid(user);
  const threads = useThreads();

  if (threads.state === 'loading') return <Centered />;
  if (threads.state === 'error') return <Notice tone="error">{threads.message}</Notice>;
  if (threads.data.length === 0) {
    return (
      <EmptyState
        icon={<ChatIcon size={34} />}
        title="אין עדיין שיחות"
        body="נרשמים יכולים לכתוב לכם מהקישור האישי שלהם. כדי לפתוח שיחה בעצמכם — היכנסו לעבודה, לחצו על נרשם ואז על 'שיחה'."
      />
    );
  }
  return (
    <ul className="overflow-hidden rounded-card border border-line bg-surface">
      {threads.data.map((t) => (
        <ThreadRow key={t.id} t={t} unread={isThreadUnread(t, uid)} />
      ))}
    </ul>
  );
}

function ThreadRow({ t, unread }: { t: ThreadView; unread: boolean }) {
  const at = tsToDate(t.lastMessageAt);
  return (
    <li className="border-b border-line last:border-b-0">
      <Link to={`/messages/${t.id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-sunken/60 active:bg-sunken">
        <span className={`mt-1 size-2.5 shrink-0 rounded-full ${unread ? 'bg-accent' : 'bg-transparent'}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className={`truncate text-[15px] ${unread ? 'font-bold' : 'font-semibold'}`}>{t.contractorName}</span>
            {at && <time className="tabular shrink-0 text-[12px] text-ink-3">{formatWhen(at)}</time>}
          </div>
          <div className="truncate text-[13px] text-ink-3">
            {REGISTRANT_TYPE_LABELS[t.registrantType]} · {t.jobName} · {formatJobDate(t.jobDate)}
            {t.closed && ' · סגורה'}
          </div>
          <p className={`mt-0.5 truncate text-[14px] ${unread ? 'text-ink' : 'text-ink-2'}`}>
            {t.lastSender === 'admin' && <span className="text-ink-3">אנחנו: </span>}
            {t.lastMessageText}
          </p>
        </div>
        {unread && <span className="sr-only">לא נקרא</span>}
      </Link>
    </li>
  );
}

/** שיחה אחת עם נרשם. אפשר לפתוח גם לפני שנשלחה הודעה (מכרטיס הנרשם). */
export function ThreadPage() {
  const { threadId = '' } = useParams();
  const parsed = parseThreadId(threadId);
  const { user } = useAdmin();
  const uid = readerUid(user);
  const toast = useToast();
  const jobId = parsed?.jobId ?? '';
  const job = useJob(jobId);
  const regs = useJobRegistrations(jobId);
  const thread = useThread(threadId);
  const messages = useThreadMessages(threadId);

  const t = thread.state === 'ready' ? thread.data : null;
  const unread = t ? isThreadUnread(t, uid) : false;
  // סימון כנקרא כשנכנסים לשיחה וכשמגיעה הודעה חדשה מהנרשם בזמן שהיא פתוחה
  useEffect(() => {
    if (unread) void adminApi(user).markThreadRead(threadId).catch(() => undefined);
  }, [unread, threadId, user]);

  const lines: ChatLine[] = useMemo(
    () =>
      messages.state === 'ready'
        ? messages.data.map((m) => ({ id: m.id, mine: m.sender === 'admin', authorName: m.authorName, text: m.text, at: tsToDate(m.createdAt) }))
        : [],
    [messages],
  );

  if (!parsed) return <Notice tone="error">השיחה לא נמצאה.</Notice>;
  if (job.state === 'loading' || regs.state === 'loading') return <Centered />;
  if (job.state === 'error') return <Notice tone="error">{job.message}</Notice>;

  const j = job.data;
  const reg = regs.state === 'ready' ? regs.data.find((r) => r.id === parsed.contractorId) ?? null : null;
  const name = reg?.fullName ?? t?.contractorName ?? 'נרשם';
  const archived = !j || j.status !== 'active' || !!t?.closed;
  const removed = !!j && regs.state === 'ready' && !reg;

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/messages" className="mb-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-ink-2 hover:text-ink">
        <BackIcon size={17} />
        הודעות
      </Link>
      <header className="mb-3 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-[21px] font-bold leading-tight">
            {reg && <TypeChip type={reg.registrantType} />}
            <span className="truncate">{name}</span>
          </h1>
          {j ? (
            <Link to={`/jobs/${j.id}`} className="text-[14px] text-brand underline-offset-2 hover:underline">
              {j.name} · {formatJobDate(j.date)}
            </Link>
          ) : (
            <span className="text-[14px] text-ink-3">{t?.jobName ?? 'העבודה נמחקה'}</span>
          )}
        </div>
      </header>
      <p className="mb-3 text-[13px] text-ink-3">כל המנהלים רואים את השיחה ויכולים לענות. {name.split(' ')[0]} רואה את התשובות בקישור האישי שלו.</p>
      <ChatView
        lines={lines}
        loading={messages.state === 'loading'}
        error={messages.state === 'error' ? <Notice tone="error">{messages.message}</Notice> : undefined}
        closed={archived || removed}
        closedText={removed ? 'הנרשם הוסר מהעבודה — אי אפשר לשלוח לו הודעות.' : 'העבודה בארכיון — השיחה פתוחה לקריאה בלבד.'}
        emptyText={`עוד אין הודעות. ההודעה שתכתבו תופיע בקישור האישי של ${name.split(' ')[0]}.`}
        onSend={async (text) => {
          try {
            await adminApi(user).sendThreadMessage(threadId, text);
            return true;
          } catch (err) {
            toast(errorMessage(err), 'error');
            return false;
          }
        }}
      />
    </div>
  );
}

function TeamChat() {
  const { user } = useAdmin();
  const uid = readerUid(user);
  const toast = useToast();
  const chat = useTeamChat();
  const readAt = useTeamReadAt();

  const lastOther = chat.state === 'ready' ? [...chat.data].reverse().find((m) => m.authorUid !== uid) : undefined;
  const lastOtherAt = tsToDate(lastOther?.createdAt)?.getTime() ?? 0;
  const read = readAt.state === 'ready' ? (readAt.data?.getTime() ?? 0) : null;
  const needsRead = read !== null && lastOtherAt > read;
  useEffect(() => {
    if (needsRead) void adminApi(user).markTeamRead().catch(() => undefined);
  }, [needsRead, lastOtherAt, user]);

  const lines: ChatLine[] = useMemo(
    () =>
      chat.state === 'ready'
        ? chat.data.map((m) => ({ id: m.id, mine: m.authorUid === uid, authorName: m.authorName, text: m.text, at: tsToDate(m.createdAt) }))
        : [],
    [chat, uid],
  );

  return (
    <>
      <p className="mb-3 text-[13px] text-ink-3">צ'אט פנימי של המנהלים. הנרשמים לא רואים אותו.</p>
      <ChatView
        lines={lines}
        loading={chat.state === 'loading'}
        error={chat.state === 'error' ? <Notice tone="error">{chat.message}</Notice> : undefined}
        emptyText="עוד אין הודעות בצ'אט המנהלים."
        placeholder="הודעה לצוות…"
        onSend={async (text) => {
          try {
            await adminApi(user).sendTeamMessage(text);
            return true;
          } catch (err) {
            toast(errorMessage(err), 'error');
            return false;
          }
        }}
      />
    </>
  );
}

/** "+": רשימת כל הנרשמים בעבודות הפעילות, עם חיפוש. בחירה פותחת את השיחה איתם באותה עבודה. */
function NewChatDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const list = useActiveRegistrants(open);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [type, setType] = useState<'all' | 'contractor' | 'worker'>('all');

  const groups = useMemo(() => {
    if (list.state !== 'ready') return [];
    const term = search.trim();
    const digits = term.replace(/\D/g, '');
    const shown = list.data.filter(
      (r) =>
        (type === 'all' || r.registrantType === type) &&
        (!term || r.fullName.includes(term) || r.jobName.includes(term) || (digits.length >= 3 && `0${r.phone.slice(4)}`.includes(digits))),
    );
    const byJob = new Map<string, typeof shown>();
    for (const r of shown) byJob.set(r.jobId, [...(byJob.get(r.jobId) ?? []), r]);
    return [...byJob.values()];
  }, [list, search, type]);

  return (
    <Dialog open={open} onClose={onClose} title="שיחה חדשה">
      <label className="relative block">
        <span className="sr-only">חיפוש</span>
        <SearchIcon size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-ink-3" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="שם, טלפון או עבודה"
          className="block min-h-11 w-full rounded-control border border-line-strong bg-surface ps-10 pe-3 text-[16px] focus:border-brand focus:outline-none"
        />
      </label>
      <div className="mt-2 flex gap-1.5">
        {(['all', 'contractor', 'worker'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={`min-h-9 rounded-full px-3 text-[13px] font-semibold ${type === t ? 'bg-brand text-white' : 'bg-sunken text-ink-2'}`}
          >
            {t === 'all' ? 'הכל' : t === 'contractor' ? 'קבלנים' : 'עובדים'}
          </button>
        ))}
      </div>

      <div className="mt-3 max-h-[55vh] overflow-y-auto">
        {list.state === 'loading' && <Centered />}
        {list.state === 'error' && <Notice tone="error">{list.message}</Notice>}
        {list.state === 'ready' && groups.length === 0 && (
          <p className="py-6 text-center text-[14px] text-ink-3">{list.data.length === 0 ? 'אין נרשמים בעבודות הפעילות.' : 'לא נמצאו נרשמים מתאימים.'}</p>
        )}
        {groups.map((g) => (
          <section key={g[0]!.jobId} className="mb-3">
            <h3 className="sticky top-0 z-10 bg-surface py-1.5 text-[13px] font-semibold text-ink-3">
              {g[0]!.jobName} · {formatJobDate(g[0]!.jobDate)}
            </h3>
            <ul className="divide-y divide-line rounded-control border border-line">
              {g.map((r) => (
                <li key={r.contractorId}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`/messages/${threadIdFor(r.jobId, r.contractorId)}`);
                    }}
                    className="flex min-h-12 w-full items-center gap-3 px-3 py-2 text-start hover:bg-sunken/60"
                  >
                    <TypeChip type={r.registrantType} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold">{r.fullName}</span>
                      <span className="ltr block text-start text-[12px] text-ink-3">{formatPhoneForDisplay(r.phone)}</span>
                    </span>
                    <ChatIcon size={18} className="text-brand" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Dialog>
  );
}

function Centered() {
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
}
